import React, { FC, memo, useCallback, useState } from 'react';

import { ModalTypes, ModalProps } from 'application/providers/ModalProvider';
import { RouterUtils, useNavigate, useParams } from 'application/providers/RouterProvider';
import { STREAMS_CATEGORY_PATH } from 'application/consts';
import AnalyticsService, {
  PointParamNameAMP,
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { TEXTS as POINTS_TEXTS } from 'application/pages/PointsPage/renderer/texts';

import { UserFeatures } from 'domain/api/graphql/generated';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import Info, { InfoTypes } from '@zvuk-b2b/react-uikit/ui/Info';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';

import useSelectPlayerModalViewModel from './useSelectPlayerModalViewModel';
import { TEXTS } from './texts';
import { DeviceBindStatus, DeviceTypesInFlow, PlayerTypesInFlow, SelectPlayerFlowType } from '../types';
import { PLAYER_TO_DEVICE_TYPE, MODAL_MODE_BY_DEVICE_TYPE } from './consts';
import PlayerTypeRadio from './PlayerTypeRadio';
import { useOnboardingContext } from '../useOnboardingContext';
import { useOnboardingActionContext } from '../useOnboardingActionContext';

import styles from './index.module.scss';

const SelectPlayerModal: FC<ModalProps[ModalTypes.SELECT_PLAYER_MODAL]> = ({ openModal, replaceModal, close }) => {
  const { isTemplateMode, flowType, currentPoint } = useOnboardingContext();
  const { setCurrentPointDevice, clearCurrentPoint } = useOnboardingActionContext();

  const navigate = useNavigate();
  const { isIOS, isSafariDesktop } = useMedia();
  const notification = useNotificationContext();
  const pathParams = useParams<{ id?: string }>();
  const targetStreamId = pathParams.id;

  const {
    playersData,
    isChangeStreamLoading,
    isCopyStreamTemplateLoading,
    isUserFeaturesOnboardingPassLoading,
    onboardingPassedRequest,
    refetchFeaturesOnboarding,
    pointSetDevice,
  } = useSelectPlayerModalViewModel();

  const [selectedPlayer, setSelectedPlayer] = useState<PlayerTypesInFlow>(PlayerTypesInFlow.WEBPLAYER);
  const [confirmSelectedPlayerLoading, setConfirmSelectedPlayerLoading] = useState<boolean>(false);

  const pointDevice = currentPoint.device?.type;
  const pointDeviceBound = (currentPoint?.device?.bindStatus === DeviceBindStatus.BINDED && pointDevice) || undefined;
  const haveBoundDevice =
    flowType === SelectPlayerFlowType.CHANGE_DEVICE && !!currentPoint?.stream?.id && !!pointDeviceBound;

  const isLoading =
    isChangeStreamLoading ||
    isCopyStreamTemplateLoading ||
    isUserFeaturesOnboardingPassLoading ||
    confirmSelectedPlayerLoading;

  const modalTitle = haveBoundDevice ? TEXTS.MODAL_WARNING_TITLE : TEXTS.MODAL_UPDATED_TITLE;

  const onPlayerChange = (playerType: PlayerTypesInFlow) => {
    setSelectedPlayer(playerType);
    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_SELECT_PLAYER_RADIO_CLICKED, {
      [StreamBindFlowParamNameAMP.PLAYER_TYPE]: playerType,
    });
  };

  const onBindStream = async () => {
    const streamId = targetStreamId;

    if (isTemplateMode) {
      navigate(
        RouterUtils.generatePath(STREAMS_CATEGORY_PATH, {
          category: 'saved',
          collectionId: 0,
          id: streamId!,
        })
      );
    }

    const isOk = await onboardingPassedRequest({ feature: UserFeatures.WarningStreamFirstStart });

    if (!isOk) {
      throw new Error();
    }

    refetchFeaturesOnboarding();
  };

  const handleUpdateClick = async (pointId: string) => {
    try {
      const { ok, deviceType, requiredVersionUrl } = await pointSetDevice({
        pointId,
        deviceBindingCode: undefined,
        createWebPlayer: true,
      });

      if (ok && deviceType) {
        AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_PLAYER_TYPE_BOUND, {
          [StreamBindFlowParamNameAMP.PLAYER_TYPE]: deviceType,
          [PointParamNameAMP.POINT_ID]: pointId,
        });
      }

      return requiredVersionUrl;
    } catch (err) {
      notification.showErrorNotification({
        children: POINTS_TEXTS.PLAYER_CHANGE_DEVICE_MODAL_UNKNOWN_ERROR,
      });
    }

    return undefined;
  };

  const onSelectWebPlayer = useCallback(async () => {
    const requiredVersionUrl = await handleUpdateClick(currentPoint?.id || '');

    if (currentPoint?.isEnabled === true && requiredVersionUrl && flowType === SelectPlayerFlowType.BIND_STREAM) {
      window.open(requiredVersionUrl, isIOS || isSafariDesktop ? '_top' : '_blank');
      clearCurrentPoint();
      close();

      return;
    }

    setCurrentPointDevice({ requiredVersionUrl, type: DeviceTypesInFlow.WEBPLAYER });

    if (flowType === SelectPlayerFlowType.CREATE_POINT || flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      replaceModal(ModalTypes.DEVICE_BIND_SUCCESS, {});

      return;
    }

    if (currentPoint?.isEnabled === false) {
      replaceModal(ModalTypes.PAYMENT_REQUIREMENT, {});
    } else {
      replaceModal(ModalTypes.STREAM_BIND_SUCCESS, {});
    }
  }, [currentPoint, flowType, setCurrentPointDevice, clearCurrentPoint]);

  const onSelectPlayer = async () => {
    setCurrentPointDevice({ type: PLAYER_TO_DEVICE_TYPE[selectedPlayer] });
    replaceModal(MODAL_MODE_BY_DEVICE_TYPE[selectedPlayer], {});
  };

  const handleClick = async () => {
    setConfirmSelectedPlayerLoading(true);

    if (selectedPlayer === PlayerTypesInFlow.WEBPLAYER && flowType === SelectPlayerFlowType.BIND_STREAM) {
      await onBindStream();
    }

    AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_SELECT_PLAYER_CLICKED, {
      [StreamBindFlowParamNameAMP.PLAYER_TYPE]: selectedPlayer,
    });

    if (selectedPlayer === PlayerTypesInFlow.WEBPLAYER) {
      await onSelectWebPlayer();
    } else {
      await onSelectPlayer();
    }

    setConfirmSelectedPlayerLoading(false);
  };

  const onConfirmBack = () => {
    openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {
      afterClose: () => {
        setTimeout(() => {
          if (flowType === SelectPlayerFlowType.BIND_STREAM) {
            openModal(ModalTypes.STREAM_BIND, {});
          } else {
            openModal(ModalTypes.POINT_CREATE, {});
          }
        }, 0);
      },
    });
  };

  const onStop = () => {
    if (flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      close();
      clearCurrentPoint();
    } else {
      onConfirmBack();
    }
  };

  const onClose = () => {
    if (flowType === SelectPlayerFlowType.CHANGE_DEVICE) {
      close();
      clearCurrentPoint();
    } else {
      openModal(ModalTypes.STREAM_STOP_BINDING_FLOW_WARNING, {});
    }
  };

  return (
    <ModalScreen
      title={modalTitle}
      titlePlacement="left"
      onClose={onClose}
      actionButtons={[
        <Button
          key="button_select_player_success"
          elementId="button_select_player_success"
          type={ButtonType.PRIMARY}
          isLoading={isLoading}
          onClick={handleClick}>
          {haveBoundDevice ? TEXTS.SUCCESS_CHANGE_BUTTON : TEXTS.SUCCESS_BUTTON}
        </Button>,
        <Button
          key="button_select_player_cancel"
          elementId="button_select_player_cancel"
          type={ButtonType.SECONDARY}
          isLoading={isLoading}
          onClick={onStop}>
          {TEXTS.CANCEL_BUTTON}
        </Button>,
      ]}>
      {currentPoint.address && <span>{TEXTS.MODAL_ADDRESS_TITLE(currentPoint.address)}</span>}

      {haveBoundDevice && (
        <Info
          className={styles.SelectPlayerModalWarning}
          type={InfoTypes.ERROR}>
          {TEXTS.CHANGE_DEVICE_TYPE_WARNING}
        </Info>
      )}

      {playersData.map((player) => (
        <PlayerTypeRadio
          player={player}
          selectedPlayer={selectedPlayer}
          pointDevice={pointDeviceBound}
          onPlayerChange={onPlayerChange}
          haveBoundDevice={haveBoundDevice}
          key={player.type}
        />
      ))}
    </ModalScreen>
  );
};

export default memo(SelectPlayerModal);
